/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
/* eslint-disable no-constant-binary-expression */
import { formatMoney } from '@shopify/theme-currency'

import { convertToHandle } from '~/helpers/convert'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Formats response from product.
 * - Any updates here must be reflected in formatNostoProduct().
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response, collection = false) {
  const product = collection ? response : response.product
  const url = collection
    ? `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}/products/${product.handle}`
    : `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.giftCard,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    media: collection ? false : formatMedia(product),
    options: collection ? false : formatOptions(product),
    price: formatPrice(product.priceRange),
    tags: product.tags,
    title: product.title,
    type: product.productType,
    url,
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.variants = formatVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  /**
   * Product badge.
   */
  formattedProduct.badge = (formattedProduct.tags.find((tag) => tag.includes('Badge:')) || '')
    .replace('Badge:', '').trim() || false

  /**
   * Format metafields.
   * - Add each metafield to object, then add case.
   */
  const metafields = {
    colours: false,
    engraving_character_limit: false,
  }

  Object.keys(metafields).forEach((key) => {
    const object = product.metafields.find((metafield) => metafield?.key === key)

    if (!object) {
      return
    }

    let value = object.value

    switch (key) {
      case 'colours':
        value = JSON.parse(object.value)
        break
      case 'engraving_character_limit':
        value = JSON.parse(object.value)
    }

    metafields[key] = value
  })

  /**
   * Format metafields to switch from snake case to camel case.
   * - Hardcoding to avoid potential errors.
   */
  Object.defineProperty(
    metafields,
    'engravingCharacterLimit',
    Object.getOwnPropertyDescriptor(metafields, 'engraving_character_limit'),
  )

  delete metafields.engraving_character_limit

  return {
    ...formattedProduct,
    ...metafields,
  }
}

/**
 * Formats price.
 * @param {Boolean} loading - Loading state.
 * @param {Object} price - Price response.
 * @returns {Object}
 */
export function formatPrice(price, loading = false) {
  return {
    amount: Number(price?.minVariantPrice.amount) * 100 ?? false,
    currencyCode: price?.minVariantPrice.currencyCode,
    loading,
    max: Number(price?.maxVariantPrice.amount) * 100 ?? false,
    min: Number(price?.minVariantPrice.amount) * 100 ?? false,
    varies: price?.maxVariantPrice.amount !== price?.minVariantPrice.amount,
  }
}

/**
 * Formats images.
 * @param {Object} product - GraphQL response.
 * @param {Array} images - Direct array of images, for formatting single images.
 * @returns {Array}
 */
function formatImages(product, images) {
  const imagesArray = images ? images : transformEdges(product.images)

  const formattedImages = imagesArray.map((image) => {
    if (!image.url) {
      return {}
    }

    const linkedVariant = product?.variants?.length
      ? transformEdges(product?.variants).find((variant) => {
        return variant.image.id === image.id
      })
      : false

    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(image.id),
      src: image.url,
      variant: decodeApiId(linkedVariant?.id) || false,
      width: image.width,
    }
  })

  if (images) {
    return formattedImages[0]
  }

  return formattedImages
}

/**
 * Formats media.
 * @param {Oject} product - GraphQL response.
 * @returns {Array}
 */
function formatMedia(product) {
  return transformEdges(product.media).map((media, index) => {
    const formattedMedia = {
      alt: media.alt,
      id: decodeApiId(media.id) || false,
      mediaType: media.mediaContentType.toLowerCase(),
      position: index + 1,
    }

    switch (formattedMedia.mediaType) {
      case 'external_video':
        formattedMedia.embedUrl = media.embedUrl
        formattedMedia.host = media.host
        formattedMedia.embedUrl = media.embedUrl
        formattedMedia.previewImage = formatImages(false, [media.previewImage])
        break

      case 'image':
        formattedMedia.image = formatImages(false, [media.image])
        break

      case 'model':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            mimeType: source.mimeType,
            url: source.url,
          }
        })
        break

      case 'video':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            height: source.height,
            mimeType: source.mimeType,
            url: source.url,
            width: source.width,
          }
        })
        break
    }

    return formattedMedia
  })
}

/**
 * Formats options.
 * @param {Object} product - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  return product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Formats variants.
 * @param {Object} product - GraphQL response.
 * @param {Boolean} isGiftCard - Product type is gift card.
 * @returns {Array}
 */
function formatVariants(product, isGiftCard) {
  return transformEdges(product.variants).map((variant) => {
    const formattedMetafields = {}

    variant.metafields.forEach((metafield) => {
      if (!metafield) {
        return
      }

      formattedMetafields[metafield.key] = metafield.value
    })

    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: Number(variant.compareAtPrice?.amount) * 100 ?? false,
        currencyCode: variant.compareAtPrice?.currencyCode ?? false,
      },
      id: decodeApiId(variant.id),
      image: decodeApiId(variant.image?.id) ?? false,
      inventory: variant.quantityAvailable,
      metafields: formattedMetafields,
      price: {
        amount: Number(variant.price.amount) * 100 ?? false,
        currencyCode: variant.price.currencyCode ?? false,
      },
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
    }

    /**
     * Wait for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.title

    return formattedVariant
  })
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response) {
  const collection = response.collection

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    pageInfo: {
      ...collection.products?.pageInfo,
      currentPage: 1,
    },
    title: collection.title,
    url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}`,
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
    const product = productResponse

    return formatProduct(product, collection)
  })

  return formattedCollection
}

/**
 * Structures the payload data for Klaviyo.
 *
 * @param {FormData} data - input data from form.
 * @returns {Object}
 */
export function formatKlaviyoData(data) {
  const formattedData = {
    type: 'subscription',
    attributes: {
      list_id: data.get('g'),
      custom_source: data.get('$source'),
      email: data.get('email'),
    },
  }

  return formattedData
}

/**
 * Formats response from menu.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - GraphQL response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
        prefix,
        previousHandle: handle,
        response: object.items,
        tier: tier + 1,
      })
      : []

    return {
      handle,
      links,
      tier,
      title: object.title,
      url: object.url,
    }
  })
}

/**
 * Formats boost options
 * @param {Object} product
 * @returns {Object}
 */
function formatBoostOptions(product) {
  return product.options_with_values.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values.map((value) => value.title),
    }
  })
}

/**
 * Formats boost images
 * @param {Object} product
 * @returns {Object}
 */
function formatBoostImages(product) {
  return product.images_info.map((image) => {
    return {
      alt: image.alt,
      height: image.height,
      id: image.id,
      src: image.src,
      width: image.width,
    }
  })
}

/**
 * Format boost variants
 * @param {Object} product
 * @param {Boolean} isGiftCard
 * @returns {Array}
 */
function formatBoostVariants(product, isGiftCard) {
  return product.variants.map((variant) => {
    const image = product.images_info.find((imageInfo) => {
      return imageInfo.src === variant.image
    })

    const formattedVariant = {
      available: variant.available,
      id: variant.id,
      image: image ? image.id : null,
      inventory: variant.inventory_quantity,
      price: {
        amount: Number(variant.price) * 100,
      },
      selectedOptions: variant.merged_options.map((option) => {
        const data = option.split(':')
        return {
          name: data[0],
          value: data[1],
        }
      }),
    }

    /**
     * Want for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat).replaceAll(',', '')
      : variant.title

    return formattedVariant
  })
}

/**
 * Formats product from Boost API call.
 * @param {Object} product
 * @returns {Object}
 */
export function formatBoostProduct(product, page) {
  const url = cnvs.routes.root === '/'
    ? `//${location.host}/products/${product.handle}`
    : `//${location.host}${cnvs.routes.root}/products/${product.handle}`

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatPrice({
      maxVariantPrice: {
        amount: product.compare_at_price_max,
      },
      minVariantPrice: {
        amount: product.compare_at_price_min,
      },
    }),
    description: product.body_html,
    handle: product.handle,
    page,
    id: product.id,
    images: formatBoostImages(product),
    options: formatBoostOptions(product),
    price: formatPrice({
      maxVariantPrice: {
        amount: product.price_max,
      },
      minVariantPrice: {
        amount: product.price_min,
      },
    }),
    metafields: product.metafields,
    tags: product.tags,
    title: product.title,
    type: product.product_type,
    url,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatBoostVariants(product, formattedProduct.giftCard)

  /**
   * Product badge.
   */
  formattedProduct.badge = (formattedProduct.tags.find((tag) => tag.includes('Badge:')) || '')
    .replace('Badge:', '').trim() || false

  const metafields = {
    colours: false,
  }

  if (product.metafields[0] !== null) {
    Object.keys(metafields).forEach((key) => {
      const object = product.metafields.find((metafield) => metafield.key === key)

      if (!object) {
        return
      }

      let value = object.value

      switch (key) {
        case 'colours':
          value = JSON.parse(object.value)
          break
      }

      metafields[key] = value
    })
  }

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return {
    ...formattedProduct,
    ...metafields,
  }
}

/**
 * Format image URL.
 * @param {String} src - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(src, options) {
  let filetype = src.match(/(?!\.)(?<filetype>jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)/gi)
  let version = src.match(/(?!\?|&)(?<version>v=\d+)/gi)

  if (!filetype || !version) {
    window.console.log('Failed to find image filetype or version')
    return src
  }

  filetype = filetype[0]
  version = version[0]
  const parameters = []

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove filetype.
   * - Remove sizing.
   * - Remove crop.
   * - Remove scale.
   */
  let url = src
    .split('?')[0]
    .split('//')[1]
    .replace(`.${filetype}`, '')
    .replace(/_(?:\d+)?x(?:\d+)?/g, '')
    .replace(/_crop_\w+/gi, '')
    .replace(/@\d+x/g, '')

  url = `//${url}.${filetype}?${version}`

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return
      }

      let formattedValue = value

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '')
      }

      parameters.push(`${key}=${formattedValue}`)
    })
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x')

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`)
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`)
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center')
    }
  }

  return parameters.length
    ? `${url}&${parameters.join('&')}`
    : url
}

/**
 * Format Nosto API product.
 * @param {Object} product - Nosto formatted product.
 * @returns {Object}
 */
export function formatNostoProduct(product) {
  const formattedProduct = {
    available: product.skus.some((sku) => sku.available),
    compareAtPrice: formatNostoPrice(product.skus, 'list_price', product.price_currency_code),
    description: product.description,
    handle: product.url.split('/products/')[1],
    id: Number(product.product_id),
    images: formatNostoImages(product),
    options: formatNostoOptions(product.skus),
    price: formatNostoPrice(product.skus, 'price', product.price_currency_code),
    tags: [
      ...product.tags1,
      ...product.tags2,
      ...product.tags3,
    ],
    title: product.name,
    type: product.custom_fields.productType,
    url: product.url,
    vendor: product.brand,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatNostoVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  /**
   * Product badge.
   */
  formattedProduct.badge = (formattedProduct.tags.find((tag) => tag.includes('Badge:')) || '')
    .replace('Badge:', '').trim() || false

  /**
   * Format metafields.
   * - Add each metafield to object, then add case.
   */
  const metafields = {
    colours: false,
  }

  Object.keys(metafields).forEach((key) => {
    const metafield = product.custom_fields[`product-${key}`]

    if (!metafield) {
      return
    }

    let value = metafield

    switch (key) {
      case 'colours':
        value = JSON.parse(metafield)
        break
    }

    metafields[key] = value
  })

  return {
    ...formattedProduct,
    ...metafields,
  }
}

/**
 * Format Nosto API product prices.
 * @param {Array} variants - Nosto format variants.
 * @param {String} property - Property to look at.
 * @param {String} currencyCode - Nosto currency code.
 * @return {Object}
 */
function formatNostoPrice(variants, property, currencyCode) {
  const prices = variants
    .map((variant) => variant[property])
    .sort()

  const max = isNaN(prices[prices.length - 1])
    ? false
    : Number(prices[prices.length - 1]) * 100

  const min = isNaN(prices[0])
    ? false
    : Number(prices[0]) * 100

  return {
    amount: min,
    currencyCode,
    max,
    min,
    varies: max !== min,
  }
}

/**
 * Format Nosto API images.
 * @param {Object} product - Nosto formatted product.
 * @returns {Array}
 */
function formatNostoImages(product) {
  const images = [
    product.image_url,
    ...product.alternate_image_urls,
  ]

  return images.map((src, id) => {
    return {
      alt: '',
      height: 0,
      id,
      src,
      variant: false,
      width: 0,
    }
  })
}

/**
 * Format Nosto API options.
 * @param {Array} variants - Nosto format variants.
 * @returns {Array}
 */
function formatNostoOptions(variants) {
  const options = {}

  variants.forEach((variant) => {
    Object.entries(variant.custom_fields).forEach(([name, value]) => {
      if (!options[name]) {
        options[name] = [value]
        return
      }

      options[name].push(value)
    })
  })

  return Object.entries(options).map(([name, values], id) => {
    const duplicates = []

    const formattedValues = values.filter((value) => {
      if (duplicates.includes(value)) {
        return false
      }

      duplicates.push(value)
      return true
    })

    return {
      id,
      name,
      values: formattedValues,
    }
  })
}

/**
 * Format Nosto API product variants.
 * @param {Object} product - Nosto formatted product.
 * @param {Boolean} isGiftCard - Product type is gift card.
 * @returns {Array}
 */
function formatNostoVariants(product, isGiftCard) {
  return product.skus.map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: isNaN(variant.list_price)
          ? false
          : Number(variant.list_price) * 100,
        currencyCode: product.price_currency_code,
      },
      id: Number(variant.id),
      image: false,
      inventory: 100,
      price: {
        amount: Number(variant.price) * 100 ?? false,
        currencyCode: product.price_currency_code,
      },
      selectedOptions: Object.entries(variant.custom_fields).map(([name, value]) => {
        return {
          name,
          value,
        }
      }),
    }

    /**
     * Wait for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.name

    return formattedVariant
  })
}

/**
 * Format multi-store countries.
 * @param {Object} countries - Countries storefront API response.
 * @param {Array} data.instances - Shopify instances..
 * @param {Array} data.theCountries - Countries to be prefixed with 'the'.
 * @returns {Object}
 */
export function formatCountries(countries, { instances, theCountries } = {}) {
  const formattedCountries = {}

  /**
   * Find instance which has no countries assigned.
   */
  const unassignedInstance = instances.find((instance) => {
    return !instance.countries.length
  })

  /**
   * Format country response.
   */
  countries.localization.availableCountries.forEach((country) => {
    formattedCountries[country.code] = formatCountry(
      country,
      unassignedInstance,
      theCountries,
    )
  })

  /**
   * Add instance countries to list if they're not present in list.
   * - Ignores instances with no country codes set.
   */
  instances.forEach((instance) => {
    if (!instance.countries.length) {
      return
    }

    instance.countries.forEach((instanceCountry) => {
      formattedCountries[instanceCountry.code] = formatCountry(
        instanceCountry,
        instance,
        theCountries,
        true,
      )
    })
  })

  return formattedCountries
}

/**
 * Format single country.
 * @param {Object} country - Available country object.
 * @param {Object} instance - Block setting instance object.
 * @param {Array} theCountries - Countries to be prefixed with 'the'.
 * @param {Boolean} isInstance - Use instance data format.
 * @returns {Object}
 */
function formatCountry(country, instance, theCountries, isInstance) {

  /**
   * Use correct data structure.
   */
  const currency = isInstance
    ? instance.currency.code
    : country.currency.code

  const currencySymbol = isInstance
    ? instance.currency.symbol
    : country.currency.symbol

  const languages = isInstance
    ? instance.languages
    : country.availableLanguages

  /**
   * Ensure that URL ends with '/'.
   */
  const formattedMatchedUrl = instance.url.slice(-1) === '/'
    ? instance.url
    : `${instance.url}/`

  /**
   * Set URL to false if current store includes URL.
   * - URL is used to update /localization submit action so set to false if
   *   we want to use the default submit action.
   */
  let url = false

  if (
    !formattedMatchedUrl.includes('localhost') &&
    !formattedMatchedUrl.includes(cnvs.store.permanentDomain) &&
    !formattedMatchedUrl.includes(cnvs.store.shopDomain)
  ) {
    url = formattedMatchedUrl
  }

  /**
   * Return formatted object.
   */
  return {
    country: {
      code: country.code,
      currency,
      currencySymbol,
      name: country.name,
      the: theCountries.includes(country.code),
    },
    instance: {
      nonTransactional: instance.nonTransactional,
      url,
    },
    languages,
  }
}
